import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../../components/ui/Table';
import Button from '../../../../components/ui/Button';
import { Spinner } from '../../../../components/ui/Spinner';
import AppIcon from '../../../../components/AppIcon';
import { PageLayout } from '../../../../components/ui/PageLayout';
import { TrendingUp } from 'lucide-react';
import { AddVariablePayTypeDialog, EditVariablePayTypeDialog } from './CompanyVariablePayTypeDialogs';
import {
  CompanyVariablePayTypeInput,
  CompanyVariablePayTypeView,
} from '../../../../models/payroll';
import {
  getCompanyVariablePayTypes,
  addCompanyVariablePayType,
  editCompanyVariablePayType,
  deleteCompanyVariablePayType,
} from '../../../../api/Payroll/variablePay';
import { useToast, ToastRenderer } from '../../../../hooks/useToast';

interface RootState {
  user: { company: any };
}

export default function CompanyVariablePayTypesSettings() {
  const { t } = useTranslation();
  const company = useSelector((state: RootState) => state.user.company);
  const { toast, showToast, dismissToast } = useToast();

  const [variablePayTypes, setVariablePayTypes] = useState<CompanyVariablePayTypeView[]>([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [input, setInput] = useState<CompanyVariablePayTypeInput>({} as CompanyVariablePayTypeInput);
  const [editInput, setEditInput] = useState<CompanyVariablePayTypeInput>({} as CompanyVariablePayTypeInput);
  const [editId, setEditId] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getCompanyVariablePayTypes();
      setVariablePayTypes(response.payload);
    } catch (ex) {
      console.log(ex);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenAdd = () => {
    setOpenAdd((prev) => !prev);
  };

  const handleOpenEdit = async (id: number) => {
    setLoading(true);
    try {
      const response = await getCompanyVariablePayTypes();
      const variablePayType = response.payload.find((v: CompanyVariablePayTypeView) => v.id === id);
      const newEditInput: CompanyVariablePayTypeInput = {
        name: variablePayType ? variablePayType.name : '',
        description: variablePayType ? variablePayType.description : '',
      };
      setEditInput(newEditInput);
      setEditId(id);
      setOpenEdit(true);
    } catch (ex) {
      console.log(ex);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await addCompanyVariablePayType(input);
      setOpenAdd(false);
      fetchData();
    } catch (ex) {
      console.log(ex);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    setLoading(true);
    try {
      await editCompanyVariablePayType(editId, editInput);
      setOpenEdit(false);
      fetchData();
    } catch (ex) {
      console.log(ex);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    setLoading(true);
    try {
      await deleteCompanyVariablePayType(id);
      fetchData();
    } catch (ex) {
      console.log(ex);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout
      title={t('payroll:Variable Pay Types')}
      description={t('payroll:Manage reusable variable pay definitions used in employee compensation')}
      icon={TrendingUp}
      accentColor="emerald"
      headerActions={(
        <Button variant="outline" iconName="Plus" onClick={handleOpenAdd} data-testid="add-type-btn">
          {t('payroll:Add Variable Pay Type')}
        </Button>
      )}
    >
      <div className="flex flex-col flex-1 gap-6">
      {/* Table */}
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <Table data-testid="settings-table">
          <TableHeader>
            <TableRow>
              <TableHead className="text-sm font-semibold">{t('common:Name')}</TableHead>
              <TableHead className="text-sm font-semibold">{t('payroll:Description')}</TableHead>
              <TableHead className="text-sm font-semibold text-end">{t('common:Actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {variablePayTypes.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-muted-foreground py-6">
                  {t('common:No data')}
                </TableCell>
              </TableRow>
            )}
            {loading && (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-6">
                  <Spinner size="md" />
                </TableCell>
              </TableRow>
            )}
            {!loading && variablePayTypes.map((variablePayType) => (
              <TableRow key={variablePayType.id}>
                <TableCell>{variablePayType.name}</TableCell>
                <TableCell className="text-sm">{variablePayType.description}</TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={String(t('common:Edit'))}
                      onClick={() => handleOpenEdit(variablePayType.id)}
                    >
                      <AppIcon name="Pencil" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={String(t('common:Delete'))}
                      onClick={() => handleDelete(variablePayType.id)}
                    >
                      <AppIcon name="Trash2" size={16} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Dialogs */}
      <AddVariablePayTypeDialog
        open={openAdd}
        input={input}
        onChange={(partial) => setInput({ ...input, ...partial })}
        onClose={handleOpenAdd}
        onSubmit={handleSubmit}
        loading={loading}
      />
      <EditVariablePayTypeDialog
        open={openEdit}
        input={editInput}
        onChange={(partial) => setEditInput({ ...editInput, ...partial })}
        onClose={() => setOpenEdit(false)}
        onSubmit={handleUpdate}
        loading={loading}
      />

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
      </div>
    </PageLayout>
  );
}
